'use client'

import { Globe } from 'lucide-react'
import { useTranslation } from '@/lib/i18n'
import { directionFor } from '@/lib/i18n/direction'
import { toSupportedLocale } from '@/lib/i18n/locale-preference'
import { LOCALE_LABELS, SUPPORTED_LOCALES } from '@/lib/i18n/types'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

export interface LanguageSwitcherProps {
  /**
   * Show the current language's name beside the globe. For screens without a
   * header (sign-in, onboarding), where a bare globe is easy to miss.
   */
  showLabel?: boolean
  side?: 'top' | 'bottom'
  align?: 'start' | 'center' | 'end'
}

/**
 * Switches the interface language. Offers exactly the languages that have a
 * catalogue, each written in its own name and script.
 */
export function LanguageSwitcher({ showLabel = false, side = 'bottom', align = 'end' }: LanguageSwitcherProps) {
  const { locale, setLocale, t } = useTranslation()
  const label = t('languageSwitcher.switchLanguage')

  return (
    <DropdownMenu>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              {showLabel ? (
                // The visible language name is part of the accessible name
                // (WCAG 2.5.3), so no aria-label here.
                <Button variant="ghost" size="sm" className="h-7 gap-1.5 px-2">
                  <Globe className="h-4 w-4" aria-hidden="true" />
                  <span className="sr-only">{label}</span>
                  <span lang={locale} dir={directionFor(locale)}>
                    {LOCALE_LABELS[locale]}
                  </span>
                </Button>
              ) : (
                <Button variant="ghost" size="icon" className="h-7 w-7" aria-label={label}>
                  <Globe className="h-4 w-4" aria-hidden="true" />
                </Button>
              )}
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>{label}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <DropdownMenuContent align={align} side={side}>
        <DropdownMenuRadioGroup
          value={locale}
          onValueChange={(value) => {
            const next = toSupportedLocale(value)
            if (next) setLocale(next)
          }}
        >
          {SUPPORTED_LOCALES.map((code) => (
            <DropdownMenuRadioItem key={code} value={code} className={cn(locale === code && 'font-semibold')}>
              <span className="w-6 text-xs text-muted-foreground" aria-hidden="true">
                {code.toUpperCase()}
              </span>
              <span lang={code} dir={directionFor(code)}>
                {LOCALE_LABELS[code]}
              </span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/**
 * A language switcher pinned to a screen corner, for pre-login screens that
 * have no header of their own. Logical positions, so it mirrors in Arabic.
 */
export function FloatingLanguageSwitcher({ corner = 'top-end' }: { corner?: 'top-end' | 'bottom-start' }) {
  const bottom = corner === 'bottom-start'
  return (
    <div
      data-testid="floating-language-switcher"
      className={cn(
        'fixed z-30',
        bottom
          ? 'bottom-4 start-4 rounded-full border bg-background/90 shadow-sm backdrop-blur'
          : 'top-3 end-3 sm:top-4 sm:end-4',
      )}
    >
      <LanguageSwitcher showLabel={bottom} side={bottom ? 'top' : 'bottom'} align={bottom ? 'start' : 'end'} />
    </div>
  )
}
